/**
 * Фильтры по колонкам игровых данных — чистая часть, без React и axios (её видит jest).
 * Разбор — docs/game-data.md §2d.
 *
 * Значение фильтра живёт под именем своего query-параметра и уходит в GET списка как есть.
 * Каждое значение — список строк: у обычного параметра он из одного элемента, у повторяющегося
 * (`ingredients`, `activationCost`) — из нескольких. Так одно хранилище годится для всех видов,
 * а `buildQueryParams` и URL повторяют параметр сами.
 */

import type { ColumnFilterValues } from '../../types/game-data.types';

/** Применённые фильтры: query-параметр → значения. Тип общий с сервисами — живёт в `types`. */
export type { ColumnFilterValues };

/** Ошибки фильтров с бэка (400): query-параметр → текст. */
export type ColumnFilterErrors = Readonly<Record<string, string>>;

interface FilterFieldBase {
  /** Имя query-параметра — оно же ключ в `ColumnFilterValues`. */
  param: string;
  label: string;
}

/**
 * Поле фильтра колонки:
 * - `number` — целое (или дробное, `decimal`), точное совпадение;
 * - `text` — кусок текста;
 * - `select` — енам или справочник, точное совпадение;
 * - `rows` — повторяющийся параметр из частей через «:», каждая часть необязательна (пустая — любое
 *   значение): ингредиенты и цены «id:количество», ограничения «раса:пол:класс».
 */
export type ColumnFilterField =
  | (FilterFieldBase & { kind: 'number'; decimal?: boolean })
  | (FilterFieldBase & { kind: 'text' })
  | (FilterFieldBase & { kind: 'select'; options: { value: string; label: string }[] })
  | (FilterFieldBase & {
      kind: 'rows';
      /** Части значения по порядку — колонки строки в редакторе. */
      parts: FilterRowPart[];
      addLabel: string;
      /**
       * Пустые части в конце не писать: «57:» → «57» (ингредиенты, цены). У ограничений нет —
       * там бэк ждёт все три части («2::»), как в его примере.
       */
      trimTail?: boolean;
    });

/** Часть строки условия: число или выбор из справочника (раса, пол, класс). */
export interface FilterRowPart {
  key: string;
  label: string;
  /** Справочник: выпадающий список с вариантом «любой». Нет — ввод числа. */
  options?: { id: number; title: string }[];
  /** Фиксированная ширина колонки (можно по брейкпоинтам); нет — тянется. */
  width?: number | { xs: number; sm: number };
  /** Знак перед числом — «×» у количества. */
  prefix?: string;
}

const INTEGER_RE = /^\d+$/;
const DECIMAL_RE = /^\d+(\.\d+)?$/;
const ROW_PART_RE = /^\d*$/;

/** Что можно набрать в числовом поле: цифры, у дробного — ещё одна точка (запятая станет точкой). */
export const sanitizeNumberInput = (raw: string, decimal = false): string => {
  if (!decimal) return raw.replace(/\D/g, '');
  const cleaned = raw.replace(',', '.').replace(/[^\d.]/g, '');
  const dot = cleaned.indexOf('.');
  return dot === -1 ? cleaned : cleaned.slice(0, dot + 1) + cleaned.slice(dot + 1).replace(/\./g, '');
};

/** Значение подходит полю. Пустое подходит всегда — это «фильтра нет». */
export const isFilterValueValid = (field: ColumnFilterField, value: string): boolean => {
  const trimmed = value.trim();
  if (trimmed === '') return true;
  switch (field.kind) {
    case 'number':
      return (field.decimal ? DECIMAL_RE : INTEGER_RE).test(trimmed);
    case 'rows': {
      const parts = trimmed.split(':');
      return (
        parts.length <= field.parts.length &&
        parts.every((part) => ROW_PART_RE.test(part)) &&
        parts.some((part) => part !== '')
      );
    }
    default:
      return true;
  }
};

/** «57:10» → { id: '57', count: '10' }; «:10» → { id: '', count: '10' }; «2::» → все части. */
export const splitRow = (value: string, parts: readonly FilterRowPart[]): Record<string, string> => {
  const pieces = value.split(':');
  return Object.fromEntries(parts.map((part, index) => [part.key, (pieces[index] ?? '').trim()]));
};

/**
 * Обратно в значение параметра. Все части пустые — условия нет, вернёт ''. `trimTail` — без
 * пустого хвоста: «57» (предмет в любом количестве), но «:10» (любой предмет, ровно 10).
 */
export const joinRow = (
  values: Readonly<Record<string, string>>,
  parts: readonly FilterRowPart[],
  trimTail = false,
): string => {
  const pieces = parts.map((part) => (values[part.key] ?? '').trim());
  if (pieces.every((piece) => piece === '')) return '';
  if (trimTail) {
    while (pieces.length > 1 && pieces[pieces.length - 1] === '') pieces.pop();
  }
  return pieces.join(':');
};

/** Применить правку фильтров одной колонки: пустые значения выкидываются вместе с параметром. */
export const patchColumnFilters = (
  values: ColumnFilterValues,
  patch: Readonly<Record<string, readonly string[]>>,
): ColumnFilterValues => {
  const next: Record<string, readonly string[]> = { ...values };
  Object.entries(patch).forEach(([param, list]) => {
    const clean = list.map((value) => value.trim()).filter((value) => value !== '');
    if (clean.length > 0) next[param] = clean;
    else delete next[param];
  });
  return next;
};

export const hasColumnFilters = (values: ColumnFilterValues): boolean => Object.keys(values).length > 0;

/** Задан ли фильтр хотя бы по одному из параметров. */
export const isColumnFiltered = (values: ColumnFilterValues, fields: readonly ColumnFilterField[]): boolean =>
  fields.some((field) => (values[field.param]?.length ?? 0) > 0);

/** Одинаковые ли наборы фильтров — чтобы не перезагружать список на «применить» без изменений. */
export const sameColumnFilters = (a: ColumnFilterValues, b: ColumnFilterValues): boolean => {
  const keys = Object.keys(a);
  if (keys.length !== Object.keys(b).length) return false;
  return keys.every((key) => {
    const left = a[key];
    const right = b[key];
    return !!right && left.length === right.length && left.every((value, index) => value === right[index]);
  });
};

/** Фильтры из адресной строки. Берутся только известные параметры: чужие в GET не уедут. */
export const readColumnFilters = (search: URLSearchParams, params: readonly string[]): ColumnFilterValues => {
  const values: Record<string, readonly string[]> = {};
  params.forEach((param) => {
    const list = search
      .getAll(param)
      .map((value) => value.trim())
      .filter((value) => value !== '');
    if (list.length > 0) values[param] = list;
  });
  return values;
};

/** Фильтры → параметры для `setSearchParams` и `buildQueryParams` (повторяющиеся — списком). */
export const columnFilterParams = (values: ColumnFilterValues | undefined): Record<string, string[]> => {
  const params: Record<string, string[]> = {};
  Object.entries(values ?? {}).forEach(([param, list]) => {
    if (list.length > 0) params[param] = [...list];
  });
  return params;
};

/**
 * Ошибки фильтров из ответа 400: бэк отдаёт карту «поле → ошибки» в `message`.
 * Ключ может прийти с индексом или префиксом (`ingredients[0]`, `getRecipes.ingredients`) —
 * сводим к своему параметру. `null` — ответ не про фильтры (не 400 или поля чужие).
 */
export const filterErrorsFromResponse = (
  error: unknown,
  params: readonly string[],
): ColumnFilterErrors | null => {
  const response = (error as { response?: { status?: number; data?: { message?: unknown } } } | null)?.response;
  const message = response?.data?.message;
  if (response?.status !== 400 || !message || typeof message !== 'object') return null;

  const errors: Record<string, string> = {};
  Object.entries(message as Record<string, unknown>).forEach(([key, value]) => {
    const param = params.find(
      (name) =>
        key === name ||
        key.startsWith(`${name}[`) ||
        key.startsWith(`${name}.`) ||
        key.endsWith(`.${name}`) ||
        key.includes(`.${name}[`),
    );
    if (!param) return;
    // Неверных условий в параметре бывает несколько — по сообщению на строку
    const text = (Array.isArray(value) ? value : [value]).filter(Boolean).map(String).join('\n');
    if (text) errors[param] = errors[param] ? `${errors[param]}\n${text}` : text;
  });
  return Object.keys(errors).length > 0 ? errors : null;
};

/** Поле фильтра по числу — точное совпадение. `decimal` — дробное (шанс, шаг гарантии). */
export const numberFilter = (param: string, label: string, decimal = false): ColumnFilterField => ({
  kind: 'number',
  param,
  label,
  decimal,
});

/**
 * Фильтр-список «предмет + количество» (ингредиенты, цены): «57», «57:10» или «:10» — любая часть
 * может быть пустой. Пустой хвост не пишется — «57», а не «57:».
 */
export const itemRowsFilter = (
  param: string,
  label: string,
  labels: { itemId: string; count: string; addMore: string },
): ColumnFilterField => ({
  kind: 'rows',
  param,
  label,
  parts: [
    { key: 'id', label: labels.itemId },
    // На телефоне уже: в шторке иначе не остаётся места под ID
    { key: 'count', label: labels.count, width: { xs: 110, sm: 160 }, prefix: '×' },
  ],
  addLabel: labels.addMore,
  trimTail: true,
});
